/**
 * Sticky Aside Module
 * File: sticky-aside.js
 * Description: Gestisce il comportamento sticky della sidebar laterale durante lo scroll
 */

const StickyAside = {
    aside: null,
    asideInner: null,
    container: null,
    navbar: null,
    adminBar: null,
    isSticky: false,
    isBottomStuck: false,
    navbarHeight: 0,
    adminBarHeight: 0,
    asideWidth: 0,

    /**
     * Inizializza la sidebar sticky
     * @param {string} asideSelector - Selettore CSS per il contenitore aside
     * @param {string} navbarId - ID della navbar principale
     * @param {string} adminBarId - ID della admin bar WordPress
     */
    initialize(
        asideSelector = "[data-sticky-aside]",
        navbarId = "mainNavbar",
        adminBarId = "wpadminbar"
    ) {
        this.aside = document.querySelector(asideSelector);
        if (!this.aside) {
            console.warn(`Aside con selettore "${asideSelector}" non trovato.`);
            return;
        }

        this.asideInner = this.aside.querySelector("[data-sticky-aside-inner]");
        if (!this.asideInner) {
            console.warn("Inner container [data-sticky-aside-inner] non trovato.");
            return;
        }

        this.container = this.aside.closest(".s-article-layout__grid");
        this.navbar = document.getElementById(navbarId);
        this.adminBar = document.getElementById(adminBarId);

        this.calculateDimensions();
        this.attachScrollListener();
        this.attachResizeListener();

        console.log("StickyAside → Inizializzato ✓");
    },

    /**
     * Calcola le dimensioni necessarie
     */
    calculateDimensions() {
        this.navbarHeight = this.navbar ? this.navbar.offsetHeight : 0;
        this.adminBarHeight = this.adminBar ? this.adminBar.offsetHeight : 0;
        this.asideWidth = this.aside.offsetWidth;
    },

    /**
     * Calcola l'offset top considerando navbar e admin bar
     */
    getTopOffset() {
        // La navbar è sempre sticky (CSS su #mainSiteHeader): l'offset include
        // sempre la sua altezza.
        let offset = 40; // spacing di base (allineato a scroll-to-anchor extraOffset: 40)

        offset += this.navbarHeight;

        if (this.adminBar) {
            offset += this.adminBarHeight;
        }

        return offset;
    },

    /**
     * Gestisce lo scroll
     */
    handleScroll() {
        if (!this.aside || !this.asideInner || !this.container) return;

        const asideRect = this.aside.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();
        const asideInnerHeight = this.asideInner.offsetHeight;
        const topOffset = this.getTopOffset();
        const currentWidth = this.aside.getBoundingClientRect().width;

        // Calcola quando la sidebar raggiunge il top della viewport
        const shouldBeSticky = asideRect.top <= topOffset;

        // Calcola l'altezza massima disponibile
        const maxHeight = window.innerHeight - topOffset - 24; // 24px bottom spacing

        // Calcola il punto in cui la sidebar dovrebbe fermarsi (fondo del container - altezza sidebar)
        const containerBottom = containerRect.bottom;
        const calculatedTop = containerBottom - asideInnerHeight;
        
        // Verifica se la sidebar dovrebbe essere ancorata al fondo
        const shouldBeBottomStuck = shouldBeSticky && calculatedTop < topOffset;

        // STATO 1: Posizione normale (inizio pagina)
        if (!shouldBeSticky) {
            if (this.isSticky || this.isBottomStuck) {
                this.deactivateAll();
            }
        }
        // STATO 3: Ancorata al fondo del container (position: fixed con top calcolato)
        else if (shouldBeBottomStuck) {
            if (!this.isBottomStuck) {
                this.activateBottomStuck(currentWidth, calculatedTop);
            } else {
                // Aggiorna il top dinamicamente mentre scorre
                this.asideInner.style.top = `${calculatedTop}px`;
            }
        }
        // STATO 2: Sticky normale (scrolling nel mezzo)
        else if (shouldBeSticky) {
            if (!this.isSticky) {
                this.activateSticky(topOffset, maxHeight, currentWidth);
            } else {
                // Aggiorna valori se già in stato sticky
                this.asideInner.style.top = `${topOffset}px`;
                this.asideInner.style.maxHeight = `${maxHeight}px`;
            }
        }
    },

    /**
     * Attiva lo stato sticky
     */
    activateSticky(topOffset, maxHeight, width) {
        // Imposta la larghezza PRIMA di cambiare position per evitare flash
        this.asideInner.style.width = `${width}px`;

        // Usa requestAnimationFrame per un rendering più fluido
        requestAnimationFrame(() => {
            this.asideInner.style.position = "fixed";
            this.asideInner.style.top = `${topOffset}px`;
            this.asideInner.style.maxHeight = `${maxHeight}px`;
            this.asideInner.style.overflowY = "auto";
            this.asideInner.classList.add("is-sticky");
            this.isSticky = true;
        });
    },

    /**
     * Attiva stato ancorato al fondo del container
     * Mantiene position: fixed ma calcola il top per allinearsi al fondo
     */
    activateBottomStuck(width, calculatedTop) {
        this.asideInner.style.width = `${width}px`;
        
        requestAnimationFrame(() => {
            this.asideInner.style.position = "fixed";
            this.asideInner.style.top = `${calculatedTop}px`;
            this.asideInner.style.maxHeight = "none";
            this.asideInner.style.overflowY = "";
            this.asideInner.classList.remove("is-sticky");
            this.asideInner.classList.add("is-bottom-stuck");
            this.isSticky = false;
            this.isBottomStuck = true;
        });
    },

    /**
     * Disattiva tutti gli stati (torna a posizione normale)
     */
    deactivateAll() {
        this.asideInner.style.position = "";
        this.asideInner.style.top = "";
        this.asideInner.style.width = "";
        this.asideInner.style.maxHeight = "";
        this.asideInner.style.overflowY = "";
        this.asideInner.classList.remove("is-sticky", "is-bottom-stuck");
        this.isSticky = false;
        this.isBottomStuck = false;
    },

    /**
     * Listener per lo scroll
     */
    attachScrollListener() {
        let ticking = false;

        window.addEventListener("scroll", () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    this.handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        });
    },

    /**
     * Listener per il resize
     */
    attachResizeListener() {
        let resizeTimer;

        window.addEventListener("resize", () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                this.calculateDimensions();
                if (this.isSticky || this.isBottomStuck) {
                    this.deactivateAll();
                }
                this.handleScroll();
            }, 150);
        });
    },
};

export default StickyAside;
